import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CheckPicker } from 'rsuite'
import { ItemDataType } from 'rsuite/esm/MultiCascadeTree'
import { createGlobalStyle } from 'styled-components'
import { DropdownOption } from 'types'

import {
  InputAndSubtitle,
  sharedPickerFocusStyles,
  StyledInputContainer,
  StyledLabel,
  SubtitleRowStyled,
  SubtitleTextStyled,
  TextValue,
} from './InputStyles'

export interface MultiSelectProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  $itemKey?: string
  $ariaLabelledBy?: string
  $label?: string
  $labelColor?: string
  $labelLocation?: 'top' | 'side'
  $subtitleText?: string
  $subtitleTextColor?: string
  $backgroundColor?: string
  $borderColor?: string
  units?: string
  options: DropdownOption[]
  value?: string[]
  onValueChange: (value: string[]) => void
  placeholder?: string
  mode?: 'read' | 'edit'
  disabled?: boolean
}

const CheckPickerStyle = createGlobalStyle<{
  $backgroundColor?: string
  $borderColor?: string
}>`
  ${sharedPickerFocusStyles}

  .rs-picker-default .rs-picker-toggle.rs-btn {
    padding: 0.25rem;
    border-radius: 0.25rem;
    border-color: ${({ $borderColor, theme }) => $borderColor || theme.forecast.border} !important;
    background-color: ${({ $backgroundColor, theme }) =>
      $backgroundColor || theme.forecast.cardGroupBackground} !important

  }

  .rs-checkbox-checked .rs-checkbox-control .rs-checkbox-inner:before {
    border-color: ${({ $borderColor, theme }) => $borderColor || theme.forecast.border} !important;
    background-color: ${({ $backgroundColor, theme }) =>
      $backgroundColor || theme.forecast.cardGroupBackground} !important
  }

  .rs-picker-value-count {
    background-color: ${({ theme }) => theme.forecast.background4} !important;
  }


  .rs-picker-check-menu .rs-checkbox-checked .rs-checkbox-inner {
    /* Override the checkbox checked color */
    background-color: white !important;
    border-color: ${({ theme }) => theme.forecast.border} !important;
  }

  .rs-picker-check-menu .rs-checkbox-checked .rs-checkbox-inner:after {
    /* Override the checkmark color */
    border-color: ${({ theme }) => theme.forecast.defaultText} !important;
  }

  .rs-check-item.rs-check-item-focus, .rs-check-item:focus, .rs-check-item:not(.rs-checkbox-disabled):hover {
    color: white;
  }

  .rs-input-group.rs-input-group-inside .rs-input {
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

const getArray = (
  fieldValue: string | number | readonly string[] | undefined
): string[] => {
  if (Array.isArray(fieldValue)) {
    return fieldValue as string[]
  }

  return []
}

export const MultiSelect = ({
  options,
  onValueChange,
  $label,
  $itemKey,
  $ariaLabelledBy,
  $labelColor,
  $labelLocation,
  $subtitleText,
  $subtitleTextColor,
  $backgroundColor,
  $borderColor,
  mode,
  placeholder,
  value,
  defaultValue,
  disabled,
}: MultiSelectProps) => {
  const multiSelectContainerRef = useRef<HTMLDivElement>(null)
  const [selectedValue, setSelectedValue] = React.useState<
    string[] | undefined
  >(getArray(value || defaultValue))
  const [readOnlySelectedValue, setReadOnlySelectedValue] = React.useState<
    React.ReactNode[] | undefined
  >(getArray(value || defaultValue))
  const [searchValue, setSearchValue] = useState<string>('')

  const removeOption = () => {
    setSelectedValue(undefined)
    setReadOnlySelectedValue(undefined)
    onValueChange([])
  }

  const filterFunction = useCallback(
    (
      keyword: string,
      label: React.ReactNode,
      item: ItemDataType<string | number>
    ) => {
      return (typeof label === 'string' &&
        label.toLowerCase().includes(keyword.toLowerCase())) ||
        (item.searchValues &&
          item.searchValues.some((svs: { value: string }) =>
            svs.value.toLowerCase().includes(keyword.toLowerCase())
          ))
        ? true
        : false
    },
    []
  )

  const filteredOptions = useMemo(() => {
    if (!searchValue.trim()) {
      return options
    }
    return options.filter((option) =>
      filterFunction(searchValue, option.label, option)
    )
  }, [options, filterFunction, searchValue])

  const allFilteredSelected = useMemo(() => {
    if (!selectedValue || filteredOptions.length === 0) return false
    return filteredOptions.every((option) =>
      selectedValue.includes(option.value)
    )
  }, [selectedValue, filteredOptions])

  const optionsWithSelectAll = useMemo(() => {
    const searchTerm = searchValue.trim()
    const baseLabel = allFilteredSelected
      ? `Unselect All (${filteredOptions.length})`
      : `Select All (${filteredOptions.length})`

    const selectAllOption: DropdownOption = {
      value: '__select_all__',
      label: searchTerm ? `${baseLabel} - ${searchTerm}` : baseLabel,
    }

    return [selectAllOption, ...options]
  }, [options, filteredOptions, allFilteredSelected, searchValue])

  const handleSelectAllToggle = useCallback(() => {
    if (allFilteredSelected) {
      const filteredValues = filteredOptions.map((option) => option.value)
      const newSelectedValues = (selectedValue || []).filter(
        (val) => !filteredValues.includes(val)
      )
      setSelectedValue(newSelectedValues)
      onValueChange(newSelectedValues)
    } else {
      const filteredValues = filteredOptions.map((option) => option.value)
      const newSelectedValues = [...(selectedValue || []), ...filteredValues]
      const uniqueValues = Array.from(new Set(newSelectedValues))
      setSelectedValue(uniqueValues)
      onValueChange(uniqueValues)
    }
  }, [allFilteredSelected, filteredOptions, selectedValue, onValueChange])

  const resetReadOnlyLabels = useCallback(() => {
    const readOnlyLabels: React.ReactNode[] = []
    selectedValue?.map((value) => {
      const option = options.find((option) => option.value === value)
      readOnlyLabels.push(option?.label)
    })

    setReadOnlySelectedValue(readOnlyLabels)
  }, [options, selectedValue])

  useEffect(() => {
    resetReadOnlyLabels()
  }, [options, selectedValue, resetReadOnlyLabels])

  useEffect(() => {
    if (mode === 'read') {
      resetReadOnlyLabels()
    }
    setSelectedValue(getArray(value || defaultValue))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, value, defaultValue, options])

  const formattedReadOnlyString = useMemo(() => {
    const count = readOnlySelectedValue?.length || 0
    if (count > 2 && readOnlySelectedValue) {
      return `${readOnlySelectedValue
        .slice(0, 2)
        .join(', ')} + ${count - 2} more`
    }
    return readOnlySelectedValue?.join(', ')
  }, [readOnlySelectedValue])

  const handleChange = useCallback(
    (e: string[]) => {
      if (e) {
        if (e.includes('__select_all__')) {
          handleSelectAllToggle()
        } else {
          const filteredSelection = e.filter((val) => val !== '__select_all__')
          onValueChange(filteredSelection)
          setSelectedValue(filteredSelection)
        }
      }
    },
    [handleSelectAllToggle, onValueChange, setSelectedValue]
  )

  const handleSearch = useCallback(
    (searchString: string) => {
      setSearchValue(searchString)
    },
    [setSearchValue]
  )

  const renderValue = useCallback(
    (value: string[], items: ItemDataType<string>[]) => {
      const count = items.length
      if (count > 2) {
        return `${items
          .slice(0, 2)
          .map((item) => item.label)
          .join(', ')} + ${count - 2} more`
      }
      return items.map((item) => item.label).join(', ')
    },
    []
  )

  const forId = React.useId()

  return (
    <StyledInputContainer $labelLocation={$labelLocation}>
      {$label && (
        <StyledLabel $labelColor={$labelColor} htmlFor={forId}>
          {$label}
        </StyledLabel>
      )}

      <div
        ref={multiSelectContainerRef}
        style={{ position: 'relative', width: '100%' }}>
        <InputAndSubtitle>
          {mode === 'read' ? (
            <TextValue id={forId}>{formattedReadOnlyString}</TextValue>
          ) : (
            <>
              <CheckPickerStyle
                $backgroundColor={$backgroundColor}
                $borderColor={$borderColor}
              />
              <CheckPicker
                data-testid={`custom-form-field-${$label}`}
                id={$itemKey}
                aria-label={$ariaLabelledBy ? undefined : $itemKey}
                aria-labelledby={$ariaLabelledBy ?? $itemKey}
                name={$itemKey}
                onChange={handleChange}
                onSearch={handleSearch}
                onClean={removeOption}
                searchable={true}
                searchBy={filterFunction}
                value={selectedValue}
                data={optionsWithSelectAll ?? []}
                aria-required='true'
                placeholder={placeholder}
                container={multiSelectContainerRef.current ?? undefined}
                renderValue={renderValue}
                disabled={disabled}
              />
            </>
          )}
          {mode !== 'read' && $subtitleText && (
            <SubtitleRowStyled>
              <SubtitleTextStyled $subtitleTextColor={$subtitleTextColor}>
                {$subtitleText}
              </SubtitleTextStyled>
            </SubtitleRowStyled>
          )}
        </InputAndSubtitle>
      </div>
    </StyledInputContainer>
  )
}

MultiSelect.displayName = 'MultiSelect'
